// =============================================================================
// SimSense Platform — Weather Data Parser
// =============================================================================
// Handles both raw WeekendOptions strings ("25.56 C", "3.22 km/h")
// and computed numeric format ({ airTemp: 25.56, units: "standard" }).
// Returns SI values (Celsius, m/s) for use with formatTemp/formatSpeed.

export interface ParsedWeather {
  airTempC: number | null;
  trackTempC: number | null;
  windMs: number | null;
  humidity: number | null;
  skyLabel: string | null;
  windDir: string | null;
}

function extractNum(val: any): number | null {
  if (val == null) return null;
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const m = val.match(/([\d.]+)/);
    return m ? parseFloat(m[1]) : null;
  }
  return null;
}

export function parseWeather(w: any): ParsedWeather | null {
  if (!w || typeof w !== "object" || Object.keys(w).length === 0) return null;

  const isComputed = w.units === "standard" || w.units === "metric";

  let airTempC: number | null = null;
  let trackTempC: number | null = null;
  let windMs: number | null = null;
  let humidity: number | null = null;
  const skyLabel: string | null = w.Skies || w.skies || null;
  const windDir: string | null = w.WindDirection || null;

  if (isComputed) {
    // Computed format: pre-converted numeric values with .units field
    const rawAir = w.WeatherTemp ?? w.airTemp;
    const rawTrack = w.trackTemp;
    const rawWind = w.WindSpeed ?? w.windVel;
    airTempC = rawAir != null ? (w.units === "standard" ? (rawAir - 32) * 5 / 9 : rawAir) : null;
    trackTempC = rawTrack != null ? (w.units === "standard" ? (rawTrack - 32) * 5 / 9 : rawTrack) : null;
    windMs = rawWind != null ? (w.units === "standard" ? rawWind / 2.23694 : w.units === "metric" ? rawWind / 3.6 : rawWind) : null;
    humidity = extractNum(w.RelativeHumidity ?? w.humidity);
  } else {
    // Raw WeekendOptions: string values like "25.56 C", "3.22 km/h", "45 %"
    const rawTemp = w.WeatherTemp;
    airTempC = extractNum(rawTemp);
    if (typeof rawTemp === "string" && /\d.*F/.test(rawTemp) && airTempC != null) {
      airTempC = (airTempC - 32) * 5 / 9;
    }

    trackTempC = extractNum(w.TrackTemp);

    const rawWind = w.WindSpeed;
    const windNum = extractNum(rawWind);
    if (windNum != null) {
      if (typeof rawWind === "string" && rawWind.includes("mph")) {
        windMs = windNum / 2.23694;
      } else {
        windMs = windNum / 3.6; // km/h default
      }
    }

    humidity = extractNum(w.RelativeHumidity);
  }

  return { airTempC, trackTempC, windMs, humidity, skyLabel, windDir };
}
